import { useEffect, useMemo, useRef, useState } from 'react';
import { useT } from '../../lib/i18n';
import { addDays, formatDayChip, formatTime, partOfDay, todayInZone } from '../../lib/format';
import { ScreenHeading, Skeleton } from '../shell/PortalShell';
import type { Availability, Slot, Slots } from '../../lib/types';

/**
 * Date and time picker.
 *
 * Two requests, deliberately: a cheap availability sweep colours the horizontal
 * date strip so a customer can see at a glance which days are open, and a
 * second call fetches the actual times only for the day they land on. Loading
 * every day's slots up front would be far more data for information nobody
 * reads.
 *
 * Times are grouped into morning / afternoon / evening because a long
 * undifferentiated grid of 40 buttons is hard to scan on a phone.
 */
export function TimeStep({
  availability,
  slots,
  loadingDays,
  loadingSlots,
  selectedDate,
  selectedStart,
  onPickDate,
  onPickSlot,
  onExtendRange,
}: {
  availability: Availability | null;
  slots: Slots | null;
  loadingDays: boolean;
  loadingSlots: boolean;
  selectedDate: string;
  selectedStart: string | null;
  onPickDate: (date: string) => void;
  onPickSlot: (slot: Slot) => void;
  onExtendRange: () => void;
}): JSX.Element {
  const { t, lang } = useT();
  const timezone = availability?.timezone ?? slots?.timezone ?? 'UTC';
  const stripRef = useRef<HTMLDivElement>(null);

  // Keep the chosen day in view when the strip re-renders (e.g. after loading
  // more days), otherwise the selection scrolls off on narrow screens.
  useEffect(() => {
    const el = stripRef.current?.querySelector<HTMLElement>('[data-selected="true"]');
    el?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  }, [selectedDate, availability]);

  const grouped = useMemo(() => {
    const buckets: Record<'morning' | 'afternoon' | 'evening', Slot[]> = {
      morning: [],
      afternoon: [],
      evening: [],
    };
    for (const slot of slots?.data ?? []) buckets[partOfDay(slot.start_at, timezone)].push(slot);
    return buckets;
  }, [slots, timezone]);

  const days = availability?.days ?? [];
  const today = todayInZone(timezone);

  return (
    <div className="bk-rise">
      <ScreenHeading title={t('choose_time')} />

      {loadingDays && days.length === 0 ? (
        <div className="bk-skeleton mb-5 h-20" />
      ) : (
        <div ref={stripRef} className="bk-strip mb-5" role="group" aria-label={t('choose_time')}>
          {days
            .filter((day) => day.date >= today)
            .map((day) => {
              const chip = formatDayChip(day.date, lang);
              const active = day.date === selectedDate;
              return (
                <button
                  key={day.date}
                  type="button"
                  data-selected={active}
                  disabled={!day.available}
                  onClick={() => onPickDate(day.date)}
                  title={reasonLabel(day.reason, day.holiday_name, t)}
                  className="w-[4.25rem] rounded-2xl border border-zinc-200 bg-white px-2 py-2.5 text-center transition disabled:cursor-not-allowed disabled:opacity-35"
                  style={
                    active
                      ? { borderColor: 'var(--accent)', background: 'var(--accent-soft)' }
                      : undefined
                  }
                >
                  <span className="block text-[11px] font-medium uppercase tracking-wide text-zinc-500">
                    {chip.weekday}
                  </span>
                  <span className="mt-0.5 block text-lg font-semibold leading-none text-zinc-900">
                    {chip.day}
                  </span>
                  <span className="mt-0.5 block text-[10px] text-zinc-400">{chip.month}</span>
                  <span
                    className="mx-auto mt-1.5 block h-1 w-1 rounded-full"
                    style={{ background: day.available ? 'var(--accent)' : 'transparent' }}
                    aria-hidden
                  />
                </button>
              );
            })}
          <button
            type="button"
            onClick={onExtendRange}
            className="w-[4.25rem] rounded-2xl border border-dashed border-zinc-300 px-2 py-2.5 text-xs text-zinc-500 transition hover:border-zinc-400"
          >
            {lang === 'ar' ? 'المزيد' : 'More'}
          </button>
        </div>
      )}

      {loadingSlots ? (
        <Skeleton rows={2} />
      ) : (slots?.data.length ?? 0) === 0 ? (
        <EmptyDay date={selectedDate} days={days} />
      ) : (
        <div className="space-y-5">
          {(['morning', 'afternoon', 'evening'] as const).map((part) =>
            grouped[part].length === 0 ? null : (
              <section key={part}>
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                  {t(part)}
                </h2>
                {/* Auto-fill rather than a fixed column count: 5rem is the
                    narrowest a time reads comfortably at, so a 320px phone
                    gets three per row and a 390px one gets four, without a
                    breakpoint for each handset size. */}
                <div className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(5rem,1fr))]">
                  {grouped[part].map((slot) => {
                    const active = slot.start_at === selectedStart;
                    return (
                      <button
                        key={slot.start_at}
                        type="button"
                        aria-pressed={active}
                        onClick={() => onPickSlot(slot)}
                        className="rounded-xl border border-zinc-200 bg-white py-3 text-sm font-medium text-zinc-900 transition hover:border-zinc-300"
                        style={
                          active
                            ? {
                                borderColor: 'var(--accent)',
                                background: 'var(--accent)',
                                color: 'var(--accent-contrast)',
                              }
                            : undefined
                        }
                      >
                        {formatTime(slot.start_at, timezone, lang)}
                      </button>
                    );
                  })}
                </div>
              </section>
            ),
          )}
        </div>
      )}
    </div>
  );
}

function EmptyDay({
  date,
  days,
}: {
  date: string;
  days: Availability['days'];
}): JSX.Element {
  const { t, lang } = useT();
  const day = days.find((d) => d.date === date);
  const next = days.find((d) => d.date > date && d.available);
  return (
    <div className="bk-card p-6 text-center">
      <p className="m-0 text-sm text-zinc-600">
        {day?.reason === 'holiday' && day.holiday_name
          ? day.holiday_name
          : day?.reason === 'closed'
            ? t('day_closed')
            : t('no_slots')}
      </p>
      {next && (
        <p className="mt-2 text-xs text-zinc-400">
          {lang === 'ar' ? 'أقرب يوم متاح: ' : 'Next available: '}
          {formatDayChip(next.date, lang).weekday} {formatDayChip(next.date, lang).day}{' '}
          {formatDayChip(next.date, lang).month}
        </p>
      )}
    </div>
  );
}

function reasonLabel(
  reason: string | null,
  holidayName: string | null,
  t: (key: 'day_closed' | 'day_full' | 'day_holiday') => string,
): string | undefined {
  if (!reason) return undefined;
  if (reason === 'holiday') return holidayName ?? t('day_holiday');
  if (reason === 'closed') return t('day_closed');
  if (reason === 'full') return t('day_full');
  return undefined;
}

/**
 * The window of days the strip shows. Starts at a fortnight — enough for almost
 * every booking — and the "More" chip extends it a month at a time up to the
 * tenant's horizon, so the common case never pays for a 60-day sweep.
 */
export function useVisibleRange(timezone: string, maxAdvanceDays: number): {
  from: string;
  to: string;
  extend: () => void;
} {
  const [span, setSpan] = useState(14);
  const from = todayInZone(timezone);
  const to = addDays(from, Math.min(span, maxAdvanceDays));
  return {
    from,
    to,
    extend: () => setSpan((s) => Math.min(s + 30, maxAdvanceDays)),
  };
}
